// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import { IconWrapper } from "@insite/mobius/Icon";
import { jettingAppPanelsContainerStyles } from "@stoneage/App/Components/JettingAppComponents/JettingAppPanelsContainer";
import IconReturn from "@stoneage/XDL/Icons/IconReturn";
import IconTooltip from "@stoneage/XDL/Icons/IconTooltip";
import { color, printStyles, xScreen } from "@stoneage/XDL/Utilities";
import { createLinkIconStyles } from "@stoneage/XDL/Utilities/SharedStyles/sharedStyles";
import { css } from "styled-components";

jettingAppPanelsContainerStyles.unitSelectorWrapper = {
    css: css`
        position: absolute;
        right: 0;
        top: 0;
    `,
};

jettingAppPanelsContainerStyles.spinnerWrapper = {
    css: css`
        display: flex;
        justify-content: center;
        align-items: center;
        height: 300px;
    `,
};

jettingAppPanelsContainerStyles.panelsContainer = {
    css: css`
        display: flex;
        gap: 22px;
        ${xScreen("lte", "md")} {
            flex-wrap: wrap;
        }
    `,
};
jettingAppPanelsContainerStyles.calculatorTitle = {
    variant: "h3",
    forwardAs: "h1",
    css: css`
        margin-bottom: 20px;
        ${xScreen("lte", "md")} {
            display: none;
        }
    `,
};

jettingAppPanelsContainerStyles.panelContainer = {
    css: css`
        flex-grow: 1;

        ${({ numberOfPanels }: any) => {
            if (numberOfPanels) {
                return css`
                    max-width: calc(100% / ${numberOfPanels});
                    ${xScreen("lte", "md")} {
                        width: 100%;
                        max-width: unset;
                    }
                `;
            }
        }}
        @media print {
            margin-bottom: 22px;
        }
        ${printStyles.avoidBreakInside}
    `,
};
jettingAppPanelsContainerStyles.panelWrapper = {
    css: css`
        border: 1px solid ${color("common.ui5")};
    `,
};
jettingAppPanelsContainerStyles.panelTitle = {
    css: css`
        font-size: 18px;
        line-height: 22px;
        color: ${color("common.white")};
        background: ${color("common.black")};
        padding: 12px;
        width: 100%;
    `,
};
jettingAppPanelsContainerStyles.section = {
    css: css`
        padding: 16px 12px;
        display: flex;
        flex-wrap: wrap;
        column-gap: 16px;
        &:nth-child(2n) {
            background-color: ${color("common.ui4")};
        }
        &:empty {
            display: none;
        }
    `,
};
jettingAppPanelsContainerStyles.control = {
    css: css`
        &:not(:last-child) {
            margin-bottom: 16px;
        }
        ${({ fieldWidth }: any) => {
            if (fieldWidth === 50) {
                return css`
                    width: calc(${fieldWidth}% - 8px);
                `;
            }
            return css`
                min-width: ${fieldWidth}%;
            `;
        }}
        &&&:has( > div.small-divider) {
            margin-bottom: 8px;
            margin-top: -8px;
        }
    `,
};
jettingAppPanelsContainerStyles.divider = {
    css: css`
        border-top: 1px solid ${color("common.ui5")};
        height: 1px;
        width: 100%;
    `,
};

jettingAppPanelsContainerStyles.smallDivider = {
    css: css`
        border-top: 1px solid ${color("common.ui5")};
        height: 1px;
        width: calc(100% + 24px);
        margin-left: -12px;
    `,
};

jettingAppPanelsContainerStyles.resetLink = {
    ...createLinkIconStyles(IconReturn, "default", "left", {
        icon: {
            iconProps: {
                size: 15,
                css: css`
                    margin-right: 8px;
                `,
            },
        },
    }),
};
jettingAppPanelsContainerStyles.resetLinkWrapper = {
    css: css`
        margin-top: 16px;
        margin-inline: 12px;
        padding-block: 16px;
        border-top: 1px solid ${color("common.ui5")};
        display: flex;
    `,
};

jettingAppPanelsContainerStyles.sectionTitle = {
    variant: "h4",
    forwardAs: "p",
    css: css`
        margin-bottom: 0;
        display: flex;
        align-items: center;
    `,
};
jettingAppPanelsContainerStyles.tooltip = {
    iconProps: {
        src: IconTooltip,
        color: "primary.main",
        size: 14,
        css: css`
            margin-left: 4px;
            &: hover {
                opacity: 0.33;
            }
        `,
    },
    cssOverrides: {
        tooltipClickable: css`
            display: flex;
            &[aria-expanded="true"] {
                ${IconWrapper} {
                    color: ${color("common.black")};
                }
            }
        `,
    },
};
jettingAppPanelsContainerStyles.sectionTitleWrapper = {
    css: css`
        margin-bottom: 8px;
        display: flex;
        justify-content: space-between;
        width: 100%;
    `,
};
jettingAppPanelsContainerStyles.tab = {
    css: css`
        border-radius: 0;
        justify-content: center;
        flex-grow: 1;
        &:hover,
        &:focus {
            box-shadow: none;
        }
        &:disabled {
            border-color: ${color("primary.main")};
        }
    `,
};
jettingAppPanelsContainerStyles.tabsWrapper = {
    css: css`
        width: 100%;
        display: flex;
        margin-bottom: 16px;
    `,
};

jettingAppPanelsContainerStyles.buttonsWrapper = {
    css: css`
        width: 100%;
        display: flex;
        justify-content: space-between;
        margin-top: 16px;
    `,
};
jettingAppPanelsContainerStyles.nextButton = {
    css: css`
        margin-left: auto;
    `,
};
jettingAppPanelsContainerStyles.suggestJettingSection = {
    css: css`
        margin-inline: 12px;
        padding: 12px;
        background-color: ${color("common.ui4")};
    `,
};
jettingAppPanelsContainerStyles.htmlContent = {
    css: css`
        font-size: 14px;
        line-height: 22px;
    `,
};
jettingAppPanelsContainerStyles.verifySetup = {
    css: css`
        width: 100%;
        display: flex;
        justify-content: center;     
    `,
};
